import React, { useContext, useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Switch,
  Typography,
  Tooltip,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
} from "@mui/material";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import {
  createTeamProfile,
  updateProfileExtraction,
} from "../../../services/stixify_api.ts";
import LoadingButton from "../../../components/loading_button/index.tsx";
import { useAlert } from "../../../contexts/alert-context.tsx";
import { TeamContext } from "../../../contexts/team-context.tsx";
import ExtractionInput from "./extractions.tsx";

const ai_settings_options = [
  "openai:gpt-4o",
  "openai:gpt-4o-mini",
  "openai:gpt-4-turbo",
  "openai:gpt-4",
];

interface ProfileFormProps {
  open: boolean;
  onClose: () => void;
  onAddEntry: () => void;
  entryData?: any;
}

const mapping_settings_option = [
  {
    label: "Pattern Only",
    key: "st:f:f",
  },
  {
    label: "AI with Attack Flow and Image Extraction",
    key: "ai:t:t",
  },
  {
    label: "AI no Attack Flow no image extraction",
    key: "ai:f:f",
  },
  {
    label: "AI with Attack Flow and no image extraction",
    key: "ai:t:f",
  },
  {
    label: "AI with no Attack Flow and Image Extraction",
    key: "ai:f:t",
  },
];

const ProfileForm: React.FC<ProfileFormProps> = ({
  open,
  onClose,
  onAddEntry,
  entryData, // Accept entry data for editing
}) => {
  const [loading, setLoading] = useState(false);
  const [reloadIndex, setReloadIndex] = useState(0);
  const { activeTeam } = useContext(TeamContext);
  const alert = useAlert();
  const [formData, setFormData] = useState<{
    name: string;
    extractions: string[];
    ai_settings_extractions: string[];
    ai_settings_relationships: string;
    ai_summary_provider: string;
    ai_content_check_provider: string;
    relationship_mode: string;
    extract_text_from_image: boolean;
    defang: boolean;
    ignore_image_refs: boolean;
    ignore_link_refs: boolean;
    ignore_extraction_boundary: boolean;
    ai_content_check: boolean;
    ai_create_attack_flow: boolean;
    ignore_embedded_relationships: boolean;
    ignore_embedded_relationships_sro: boolean;
    ignore_embedded_relationships_smo: boolean;
    ai_extract_if_no_incidence: boolean;
    mapping_key: string;
  }>({
    name: "",
    extractions: [],
    ai_settings_extractions: [],
    ai_settings_relationships: ai_settings_options[0],
    ai_summary_provider: ai_settings_options[0],
    ai_content_check_provider: ai_settings_options[0],
    relationship_mode: "standard",
    extract_text_from_image: true,
    defang: true,
    ignore_image_refs: true,
    ignore_link_refs: true,
    ignore_extraction_boundary: true,
    ai_content_check: false,
    ai_create_attack_flow: false,
    ignore_embedded_relationships: false,
    ignore_embedded_relationships_sro: true,
    ignore_embedded_relationships_smo: true,
    ai_extract_if_no_incidence: true,
    mapping_key: "",
  });
  const [errors, setErrors] = useState<{
    name?: string[];
    extractions?: string[];
    ai_settings_extractions?: string[];
    ai_settings_relationships?: string[];
    ai_summary_provider?: string[];
    ai_content_check_provider?: string[];
    mapping_key?: string[];
    non_field_errors?: string[];
  }>({});

  useEffect(() => {
    setFormData({
      mapping_key: entryData?.mapping_key || mapping_settings_option[0].key,
      name: entryData?.name || "",
      extractions: entryData?.stixify_profile_metadata.extractions || [],
      relationship_mode:
        entryData?.stixify_profile_metadata.relationship_mode || "standard",
      ai_settings_relationships:
        entryData?.stixify_profile_metadata.ai_settings_relationships || "",
      ai_summary_provider:
        entryData?.stixify_profile_metadata.ai_summary_provider || "",
      ai_content_check_provider:
        entryData?.stixify_profile_metadata.ai_content_check_provider || "",
      ai_settings_extractions:
        entryData?.stixify_profile_metadata.ai_settings_extractions || [],
      extract_text_from_image:
        entryData?.stixify_profile_metadata.extract_text_from_image || false,
      defang: entryData ? entryData.defang : true,
      ignore_image_refs:
        entryData ? entryData.stixify_profile_metadata.ignore_image_refs : true,
      ignore_link_refs:
        entryData ? entryData.stixify_profile_metadata.ignore_link_refs : true,
      ignore_extraction_boundary:
        entryData ? entryData.stixify_profile_metadata.ignore_extraction_boundary : true,
      ai_content_check:
        entryData?.stixify_profile_metadata.ai_content_check || false,
      ai_create_attack_flow:
        entryData?.stixify_profile_metadata.ai_create_attack_flow || false,
      ignore_embedded_relationships:
        entryData?.stixify_profile_metadata.ignore_embedded_relationships ||
        false,
      ignore_embedded_relationships_sro:
        entryData?.stixify_profile_metadata.ignore_embedded_relationships_sro ||
        true,
      ignore_embedded_relationships_smo:
        entryData ? entryData.stixify_profile_metadata.ignore_embedded_relationships_smo :
        true,
      ai_extract_if_no_incidence:
        entryData ? entryData.stixify_profile_metadata.ai_extract_if_no_incidence :
        true,
    });
    setErrors({});
    setReloadIndex(reloadIndex + 1);
  }, [open, entryData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addEntry = async (data: any) => {
    await createTeamProfile(activeTeam?.id || "", data);
  };

  const editEntry = async (data: any) => {
    await updateProfileExtraction(entryData?.id || "", data);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isAI = formData.relationship_mode === "ai";
    const { ...submitData } = {
      ...formData,
      ai_settings_relationships: isAI
        ? formData.ai_settings_relationships
        : null,
      ai_create_attack_flow: isAI && formData.ai_create_attack_flow,
      extract_text_from_image: isAI && formData.extract_text_from_image,
    };
    setLoading(true);
    try {
      if (entryData) {
        await editEntry(submitData);
      } else {
        await addEntry(submitData);
      }
      onAddEntry();
    } catch (err) {
      if (err.response.status === 400) {
        setErrors(err?.response?.data?.details || err?.response?.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExtractionChange = (value: string | string[]) => {
    setFormData((prev) => ({
      ...prev,
      extractions: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleAISettingsChange = (value: string | string[]) => {
    setFormData((prev) => ({
      ...prev,
      ai_settings_extractions:
        typeof value === "string" ? value.split(",") : value,
    }));
  };
  return (
    <Box>
      <Typography variant="h4">
        {entryData ? "Edit Profile" : "Add New Extraction Profile"}
      </Typography>

      <Typography>
        Please enter the details of the{" "}
        {entryData ? "profile to edit" : "new Extraction Profile"}. You cannot
      </Typography>

      <Box mt={4}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>Name </strong>
          <Tooltip title="[REQUIRED] The name of extraction profile. Is visible to members of this team and to any user in Stixify inside reports uploaded as TLP:Green this Extraction Profile was used with.">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <TextField
          margin="dense"
          name="name"
          label="Name"
          type="text"
          fullWidth
          value={formData.name}
          onChange={handleChange}
          required
        />
        {errors?.name?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>

      {/* Extractions */}
      <Box marginY={2}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>Selected Extractions</strong>
          <Tooltip title="[REQUIRED] These are the data types you want to be extracted from the report. Some extraction types are limited by the plan level your team is subscribed to.">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <ExtractionInput
          reloadIndex={reloadIndex}
          value={formData.extractions}
          onChange={(value) => handleExtractionChange(value)}
          showAI={true}
        ></ExtractionInput>
        {errors?.extractions?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>

      <Box marginY={2}>
        <div>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <strong>AI Extractions Settings</strong>
            <Tooltip title="[REQUIRED IF ANY AI EXTRACTIONS SELECTED] The AI model to be used for extractions.">
              <HelpCenterIcon></HelpCenterIcon>
            </Tooltip>
          </Box>
        </div>
        <FormControl fullWidth>
          <InputLabel>Extractions</InputLabel>
          <Select
            fullWidth
            multiple
            value={formData.ai_settings_extractions}
            onChange={(ev) => handleAISettingsChange(ev.target.value)}
            input={<OutlinedInput label="Extractions" />}
            renderValue={(selected) => selected.join(", ")}
          >
            {ai_settings_options.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox
                  checked={formData.ai_settings_extractions.includes(option)}
                />
                <ListItemText primary={option} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {errors?.extractions?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>

      <FormControl fullWidth margin="dense">
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>Relationship Mode</strong>
          <Tooltip title="[REQUIRED] How the relationships should be created between extractions in the report.">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <Select
          name="relationship_mode"
          value={formData.relationship_mode}
          onChange={(e) =>
            setFormData({ ...formData, relationship_mode: e.target.value })
          }
        >
          <MenuItem value="ai">AI</MenuItem>
          <MenuItem value="standard">Standard</MenuItem>
        </Select>
      </FormControl>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>AI Relationships Settings</strong>
          <Tooltip title="[REQUIRED IF ANY AI EXTRACTIONS SELECTED OR ATTACK FLOW USED] The AI model to be used for relationship generation.">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <Select
          sx={{ width: "100%" }}
          disabled={formData.relationship_mode === "standard"}
          label="AI Settings Relationships"
          name="ai_settings_relationships"
          value={
            formData.relationship_mode === "ai"
              ? formData.ai_settings_relationships
              : ""
          }
          onChange={(e) =>
            setFormData({
              ...formData,
              ai_settings_relationships: e.target.value,
            })
          }
        >
          <MenuItem value={undefined}>-</MenuItem>
          {ai_settings_options.map((extraction) => (
            <MenuItem key={extraction} value={extraction}>
              {extraction}
            </MenuItem>
          ))}
        </Select>
        {errors?.ai_settings_relationships?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                disabled={formData.relationship_mode !== "ai"}
                checked={
                  formData.relationship_mode === "ai" &&
                  formData.ai_create_attack_flow
                }
                onChange={(ev) =>
                  setFormData({
                    ...formData,
                    ai_create_attack_flow: ev.target.checked,
                  })
                }
              />
            }
            label="Create an Attack Flow"
          />
        </FormControl>
        <Tooltip title="[OPTIONAL] If using MITRE ATT&CK extractions, you can also create an Attack Flow to model the order in which the ATT&CK Tactics and Techniques are described in the report. You must use AI Relationship mode for this setting to work. Limited by the plan level your team is subscribed to.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                disabled={formData.relationship_mode !== "ai"}
                checked={
                  formData.relationship_mode === "ai" &&
                  formData.extract_text_from_image
                }
                onChange={(ev) =>
                  setFormData({
                    ...formData,
                    extract_text_from_image: ev.target.checked,
                  })
                }
              />
            }
            label="Extract Text From Image"
          />
        </FormControl>
        <Tooltip title="[OPTIONAL] Extract Text From Image. Limited by the plan level your team is subscribed to.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>

      <Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>AI Summary Provider</strong>
          <Tooltip title="[OPTIONAL] This is used to generate post summaries. In production this should always be true.">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <Select
          sx={{ width: "100%" }}
          label="AI Summary Provider"
          name="ai_summary_provider"
          value={formData.ai_summary_provider}
          onChange={(e) =>
            setFormData({ ...formData, ai_summary_provider: e.target.value })
          }
        >
          <MenuItem value={undefined}>-</MenuItem>
          {ai_settings_options.map((extraction) => (
            <MenuItem key={extraction} value={extraction}>
              {extraction}
            </MenuItem>
          ))}
        </Select>
        {errors?.ai_summary_provider?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>

      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ai_content_check}
              color="primary"
              onChange={(e) =>
                setFormData({ ...formData, ai_content_check: e.target.checked })
              }
            />
          }
          label="AI Content Check"
        />
        <Tooltip title="[REQUIRED] If set to true, the AI will try and classify the report and decide if it is related to threat intelligence. If the AI decides it is not, the post will not be processed for extractions. Should always be true in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>AI Content Check Provider</strong>
          <Tooltip title="[REQUIRED - IF AI CONTENT CHECK SET TO TRUE] The AI model to do the content check.">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <Select
          sx={{ width: "100%" }}
          label="AI Content Check Provider"
          name="ai_content_check_provider "
          value={formData.ai_content_check_provider}
          onChange={(e) =>
            setFormData({
              ...formData,
              ai_content_check_provider: e.target.value,
            })
          }
        >
          <MenuItem value={undefined}>-</MenuItem>
          {ai_settings_options.map((extraction) => (
            <MenuItem key={extraction} value={extraction}>
              {extraction}
            </MenuItem>
          ))}
        </Select>
        {errors?.ai_content_check_provider?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.defang}
              color="primary"
              onChange={(e) =>
                setFormData({ ...formData, defang: e.target.checked })
              }
            />
          }
          label="Defang"
        />
        <Tooltip title="[REQUIRED] If the text should be defanged before processing. On production this should always be true.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ignore_image_refs}
              color="primary"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ignore_image_refs: e.target.checked,
                })
              }
            />
          }
          label="Ignore Image Refs"
        />
        <Tooltip title="[REQUIRED] If set to true, any URLs inside image tags will be ignored during extraction. Should always be true in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ignore_link_refs}
              color="primary"
              onChange={(e) =>
                setFormData({ ...formData, ignore_link_refs: e.target.checked })
              }
            />
          }
          label="Ignore Link Refs"
        />
        <Tooltip title="[REQUIRED] If set to true, any URLs inside link (a href) tags will be ignored during extraction. Should always be true in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ignore_extraction_boundary}
              color="primary"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ignore_extraction_boundary: e.target.checked,
                })
              }
            />
          }
          label="Ignore Extraction Boundary"
        />
        <Tooltip title="[REQUIRED] Defines if a string boundary can generate multiple extractions (e.g. url, domain, etc). Setting to true will allow multiple extractions from the same string. Should be set to false in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>

      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ignore_embedded_relationships}
              color="primary"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ignore_embedded_relationships: e.target.checked,
                })
              }
            />
          }
          label="Ignore embedded relationships"
        />
        <Tooltip title="[REQUIRED] if true passed, this will stop ANY embedded relationships from being generated. This applies for all object types (SDO, SCO, SRO, SMO). If you want to target certain object types see ignore_embedded_relationships_sro and ignore_embedded_relationships_sro flags. This should always be set to false in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ignore_embedded_relationships_sro}
              color="primary"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ignore_embedded_relationships_sro: e.target.checked,
                })
              }
            />
          }
          label="Ignore embedded relationships (SRO)"
        />
        <Tooltip title="[REQUIRED] If true passed, will stop any embedded relationships from being generated from SRO objects. This should always be set to true in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ignore_embedded_relationships_smo}
              color="primary"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ignore_embedded_relationships_smo: e.target.checked,
                })
              }
            />
          }
          label="Ignore embedded relationships (SMO)"
        />
        <Tooltip title="[REQUIRED] If true passed, will stop any embedded relationships from being generated from SMO objects. This should always be set to true in production.">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box mb={4} sx={{ display: "flex", alignItems: "center" }}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.ai_extract_if_no_incidence}
              color="primary"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  ai_extract_if_no_incidence: e.target.checked,
                })
              }
            />
          }
          label="AI Extract if no incident"
        />
        <Tooltip title="[REQUIRED] If true passed, it will extract with AI if there is no incident">
          <HelpCenterIcon></HelpCenterIcon>
        </Tooltip>
      </Box>
      <Box sx={{ marginBottom: "2rem" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <strong>Mapping</strong>
          <Tooltip title="Map to the appropriate profile settings">
            <HelpCenterIcon></HelpCenterIcon>
          </Tooltip>
        </Box>
        <Select
          fullWidth
          value={formData.mapping_key}
          onChange={(ev) =>
            setFormData({ ...formData, mapping_key: ev.target.value })
          }
        >
          {mapping_settings_option.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>

        {errors?.mapping_key?.map((error) => (
          <Typography sx={{ color: "red" }}>{error}</Typography>
        ))}
      </Box>
      {errors?.non_field_errors?.map((error) => (
        <Typography sx={{ color: "red" }}>{error}</Typography>
      ))}

      <Button
        variant="contained"
        sx={{ textTransform: "uppercase" }}
        onClick={onClose}
        color="secondary"
      >
        Cancel
      </Button>
      <LoadingButton
        isLoading={loading}
        variant="contained"
        sx={{ textTransform: "uppercase", marginLeft: "3rem" }}
        onClick={handleSubmit}
        color="primary"
      >
        {entryData ? "Update" : "Add"}
      </LoadingButton>
    </Box>
  );
};

export default ProfileForm;
